import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import PageHeaderContainer from 'containers/PageHeader';
import { Widget } from '@spins/react-ui-library';
import OverviewTable from 'components/OverviewTable';
import {
  ACCOUNT_POD_GAINS_SERIES,
  ACCOUNT_POD_LOSSES_SERIES,
  ACCOUNT_POD_OPPORTUNITIES_SERIES,
  ITEMS_POD_GAINS_SERIES,
  ITEMS_POD_LOSSES_SERIES,
  ITEMS_POD_OPPORTUNITIES_SERIES
} from 'apollo/queries';
import { OTHER_ROUTES, ROUTES } from 'utils/routes';
import {
  GlobalFilterType,
  FilterSelectionType,
  PeriodEndWeekType,
  GlobalFilterKeyType,
  GlobalFilterKey
} from 'utils/types';
import { periodEndWeekSelector } from 'containers/AccessControl/selectors';
import SalesHighlightsWidget from 'components/SalesHighlightsWidget';
import { globalFilterSelector } from 'containers/PageHeader/selectors';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import * as globalSelectors from 'store/global/selectors';
import FilterBarContainer from 'containers/PageHeader/FilterBar';
import ReactGA from 'react-ga';
import DistributionChartWidget from 'components/Charts/Widgets/DistributionChartWidget';
import TrendChartWidget from 'components/Charts/Widgets/TrendChartWidget';
import ShareAccountChartWidget from 'components/Charts/Widgets/ShareAccountChartWidget';

interface Props {
  clientId: string;
  currentBrand: string;
  globalFilter: GlobalFilterType;
  periodEndWeek: PeriodEndWeekType;
  location: any;
  isBrandFilterable: boolean;
  onFilterChange(newFilter: FilterSelectionType, globalFilterKey: GlobalFilterKeyType): void;
  onWeeksChange(weeks: number): void;
}

const Overview: React.FC<Props> = ({
  clientId,
  currentBrand,
  globalFilter,
  periodEndWeek,
  location,
  isBrandFilterable,
  onFilterChange,
  onWeeksChange
}) => {
  const history = useHistory();

  const route = (route, history) => {
    history.push(route);
  };

  useEffect(() => {
    ReactGA.pageview(ROUTES.OVERVIEW);
  });

  useEffect(() => {
    if (location.state) {
      let { weeks, ...rest } = location.state;
      onFilterChange(rest, GlobalFilterKey.GLOBAL);
      onWeeksChange(weeks);
    }
  }, [location.state, onFilterChange, onWeeksChange]);

  const getUpcFromItemLabel = (label: string) => {
    const result = label.match(/([0-9]+)/g);
    return result[result.length - 1];
  };

  const navigateToDetailsByType = (elem: any, type: string) => {
    const key = type === 'item' ? getUpcFromItemLabel(elem.label) : elem.label;
    history.push({
      pathname: OTHER_ROUTES.DETAILS,
      search: `?${type}=${encodeURIComponent(key)}&${type}_name=${encodeURIComponent(elem.label)}`,
      state: elem
    });
  };

  return (
    <React.Fragment>
      <Row>
        <PageHeaderContainer
          globalFilterContext={GlobalFilterKey.GLOBAL}
          template="overviewDetails"
          exportPdfAvailable={true}
        />
      </Row>
      <FilterBarContainer globalFilterContext={GlobalFilterKey.GLOBAL} />
      <Row className="mt-3">
        <Col>
          <SalesHighlightsWidget
            clientId={clientId}
            currentBrand={currentBrand}
            periodEndWeek={periodEndWeek}
            globalFilter={globalFilter}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-3 pr-2">
          <TrendChartWidget globalFilter={globalFilter} />
        </Col>
        <Col md={4} className="mt-3 pl-2 pr-2">
          <ShareAccountChartWidget globalFilter={globalFilter} />
        </Col>
        <Col md={4} className="mt-3 pl-2">
          <DistributionChartWidget globalFilter={globalFilter} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <Widget>
            {{
              header: {
                title: 'Account Summary'
              },
              body: {
                content: (
                  <Row>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={globalFilter}
                        nameHeader="Accounts"
                        valueHeader="POD Gains"
                        query={ACCOUNT_POD_GAINS_SERIES}
                        {...(isBrandFilterable && {
                          onRowClick: row => navigateToDetailsByType(row, 'account')
                        })}
                        onViewAllClick={() => route(ROUTES.ACCOUNTS, history)}
                      />
                    </Col>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={globalFilter}
                        nameHeader="Accounts"
                        valueHeader="POD Losses"
                        query={ACCOUNT_POD_LOSSES_SERIES}
                        {...(isBrandFilterable && {
                          onRowClick: row => navigateToDetailsByType(row, 'account')
                        })}
                        onViewAllClick={() => route(ROUTES.ACCOUNTS, history)}
                      />
                    </Col>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={globalFilter}
                        nameHeader="Account"
                        valueHeader="Opportunities"
                        query={ACCOUNT_POD_OPPORTUNITIES_SERIES}
                        {...(isBrandFilterable && {
                          onRowClick: row => navigateToDetailsByType(row, 'account')
                        })}
                        onViewAllClick={() => route(ROUTES.ACCOUNTS, history)}
                      />
                    </Col>
                  </Row>
                )
              }
            }}
          </Widget>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <Widget>
            {{
              header: {
                title: 'Item Summary'
              },
              body: {
                content: (
                  <Row>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={globalFilter}
                        nameHeader="Items"
                        valueHeader="POD Gains"
                        query={ITEMS_POD_GAINS_SERIES}
                        {...(isBrandFilterable && {
                          onRowClick: row => navigateToDetailsByType(row, 'item')
                        })}
                        onViewAllClick={() => route(ROUTES.ITEMS, history)}
                      />
                    </Col>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={globalFilter}
                        nameHeader="Items"
                        valueHeader="POD Losses"
                        query={ITEMS_POD_LOSSES_SERIES}
                        {...(isBrandFilterable && {
                          onRowClick: row => navigateToDetailsByType(row, 'item')
                        })}
                        onViewAllClick={() => route(ROUTES.ITEMS, history)}
                      />
                    </Col>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={globalFilter}
                        nameHeader="Items"
                        valueHeader="Opportunities"
                        query={ITEMS_POD_OPPORTUNITIES_SERIES}
                        {...(isBrandFilterable && {
                          onRowClick: row => navigateToDetailsByType(row, 'item')
                        })}
                        onViewAllClick={() => route(ROUTES.ITEMS, history)}
                      />
                    </Col>
                  </Row>
                )
              }
            }}
          </Widget>
        </Col>
      </Row>
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  clientId: globalSelectors.clientIdSelector(state),
  currentBrand: globalSelectors.currentBrandSelector(state),
  globalFilter: globalFilterSelector(state),
  periodEndWeek: periodEndWeekSelector(state),
  isBrandFilterable: globalSelectors.isBrandFilterableSelector(state)
});

const mapDispatchToProps = dispatch => ({
  onFilterChange: (newFilter: FilterSelectionType, globalFilterKey: GlobalFilterKeyType) => {
    dispatch(pageHeaderActions.updateGlobalFilterArrays(newFilter, globalFilterKey));
  },
  onWeeksChange: (weeks: number) => {
    dispatch(pageHeaderActions.updateGlobalFilterWeeks(weeks));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(Overview);
